"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import Typography from "../../atoms/typography";
import { constants } from "../../../constants/pageConstants/pdpConstants";

/**
 * Description placeholder
 * @date 17/07/2024
 * @param {string} param.imageSrc - The source URL of the image to be displayed.
 * @param {string} param.zoomImageSrc - The source URL of the Zoomed image to be displayed.
 * @param {string} [param.resultDivWidth="w-96"] - The width class of the result div that shows the magnified portion of the image.
 * @param {string} [param.resultDivHeight="h-full"] - The height class of the result div that shows the magnified portion of the image.
 * @param {number} [param.lensWidth=100] - The width of the magnification lens. This width is also used as the height, ensuring the lens is a square.
 * @returns {JSX.Element} The rendered component.
 */
const ImageZoom = ({
  imageSrc,
  zoomImageSrc,
  resultDivWidth = "w-400",
  resultDivHeight = "h-full",
  lensWidth = 200,
  onImageClick,
  skuCode,
}) => {
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 1000); // or 100–300ms if you want slight delay

    return () => clearTimeout(timer);
  }, []);
  // Refs to access DOM elements
  const imgRef = useRef(null);
  const resultRef = useRef(null);
  const imageDivRef = useRef(null);

  let lens, cx, cy;

  /**
  Function to get cursor coordinates relative to the image
  @param {object} e - event
   @returns {} return x and y coordinates.
 */
  const getCursorPos = (e) => {
    let a,
      x = 0,
      y = 0;
    e = e || window;

    a = imgRef.current?.getBoundingClientRect();

    x = e.pageX - a.left;
    y = e.pageY - a.top;

    x = x - window.scrollX;
    y = y - window.scrollY;
    return { x: x, y: y };
  };

  /**
   * @param {object} e - event
  Function to move the lens based on cursor position
 */
  const moveLens = useCallback((e) => {
    if (!resultRef.current || !imgRef.current) return;
    let pos, x, y;

    e.preventDefault();

    pos = getCursorPos(e);
    if (!lens) return;

    x = pos.x - lens.offsetWidth / 2;
    y = pos.y - lens.offsetHeight / 2;

    // Ensure the lens stays within bounds of the image
    if (x > imgRef.current?.width - lens.offsetWidth) {
      x = imgRef.current?.width - lens.offsetWidth;
    }
    if (x < 0) {
      x = 0;
    }
    if (y > imgRef.current?.height - lens.offsetHeight) {
      y = imgRef.current?.height - lens.offsetHeight;
    }
    if (y < 0) {
      y = 0;
    }

    // Set lens position
    lens.style.left = x + "px";
    lens.style.top = y + "px";

    // Update result image background position for zoom effect
    resultRef.current.style.backgroundPosition = "-" + x * cx + "px -" + y * cy + "px";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
 * 
 Function to handle mouse leave event
 */
  const mouseLeavefunc = useCallback(() => {
    if (!resultRef.current || !imgRef.current) return;
    document.getElementsByClassName("img-zoom-lens")[0]?.remove();
    resultRef.current?.classList?.add("hidden");
    const image_magnifier_text = document.getElementById("image_magnifier_text");
    image_magnifier_text.innerHTML = constants.ROLL_OVER_IMAGE;
  }, []);

  /**
 *
  Function to handle mouse enter event
 */
  const mouseEnterfunc = useCallback(() => {
    if (!resultRef.current || !imgRef.current) return;
    lens = document.createElement("div");
    const image_magnifier_text = document.getElementById("image_magnifier_text");
    image_magnifier_text.innerHTML = constants.CLICK_TO_OPEN_EXPANDED_VIEW;

    lens.style.width = `${lensWidth}px`;
    lens.id = "zoomed-image-lens";
    lens.style.height = `${lensWidth}px`;
    lens.style.position = "absolute";
    lens.style.border = "1px solid #d4d4d4";
    lens.style.background = "#fff";
    lens.style.opacity = 0.6;
    lens.onclick = onImageClick;
    lens.setAttribute("class", "img-zoom-lens");

    if (resultRef.current && imgRef.current) {
      resultRef.current?.classList?.remove("hidden");
      imgRef.current?.parentElement.insertBefore(lens, imgRef.current);

      // Calculate scaling factors for zoom effect
      cx = resultRef.current?.offsetWidth / lens.offsetWidth;
      cy = resultRef.current?.offsetHeight / lens.offsetHeight;

      // Set result image properties
      resultRef.current.style.backgroundImage = "url('" + zoomImageSrc + "')";
      resultRef.current.style.backgroundSize = imgRef.current?.width * cx + "px " + imgRef.current?.height * cy + "px";
    }
    // Show result image and insert lens
  }, []);

  useEffect(() => {
    // Add event listeners on component mount
    imageDivRef.current?.addEventListener("mouseleave", mouseLeavefunc);
    imageDivRef.current?.addEventListener("mouseenter", () => {
      mouseLeavefunc();
      mouseEnterfunc();
      // Add event listeners for lens movement
      lens?.addEventListener("mousemove", moveLens);
      imgRef.current?.addEventListener("mousemove", moveLens);
    });

    // Clean-up event listeners on component unmount
    return () => {
      imageDivRef.current?.removeEventListener("mouseleave", mouseLeavefunc);
      imageDivRef.current?.removeEventListener("mouseenter", mouseEnterfunc);

      lens?.removeEventListener("mousemove", moveLens);
      imgRef.current?.removeEventListener("mousemove", moveLens);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc, mouseLeavefunc, mouseEnterfunc, moveLens]);

  return (
    <div className="relative flex" id="imageZoomContainer">
      <div>
        <div ref={imageDivRef} id="original-image-wrapper">
          <CustomImage
            id="original-image"
            src={imageSrc}
            width={540}
            height={540}
            imageRef={imgRef}
            isPriority={true}
            alt="pdp_main_image"
            // borderRadius={16}
            sizes="(max-width: 768px) 100vw, 50vw"
            itemProp="image"
            className="h-[540px] w-[540px] rounded-[16px]"
          />
        </div>
        <div className="mt-8 flex w-full">
          <Typography
            id="product-sku-code"
            Tag={"p"}
            variant="p"
            classes="text-12 font-400 text-grey-400 italic basis-1/3 flex-grow"
          >
            {skuCode || ""}
          </Typography>
          <Typography
            id="image_magnifier_text"
            Tag={"p"}
            variant="p"
            classes=" text-12 font-400 text-grey-500 basis-2/3 flex-grow"
          >
            {constants.ROLL_OVER_IMAGE || ""}
          </Typography>
        </div>
      </div>

      {isPageLoaded && (
        <div className={`pl-10`}>
          <div
            ref={resultRef}
            className={`img-zoom-result absolute hidden h-full w-full rounded-md ${resultDivHeight} ${resultDivWidth}`}
          />
        </div>
      )}
    </div>
  );
};

ImageZoom.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  resultDivWidth: PropTypes.string,
  resultDivHeight: PropTypes.string,
  lensWidth: PropTypes.number,
  zoomImageSrc: PropTypes.string,
  onImageClick: PropTypes.func,
  skuCode: PropTypes.string,
};

export default React.memo(ImageZoom);
